import { PageLayout } from '@/shared/components/PageLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { config } from '@/lib/constants'
import { content } from '@/lib/content'
import { useState } from 'react'
import {
  Mail,
  Phone,
  User,
  MessageSquare,
  MapPin,
  Clock,
  Facebook,
  Send,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage('')

    try {
      const response = await fetch(config.emailServerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setSubmitMessage(content.contact.form.messages.success)
        setFormData({ name: '', email: '', phone: '', message: '' })
      } else {
        setSubmitMessage(content.contact.form.messages.error)
      }
    } catch (error) {
      setSubmitMessage(content.contact.form.messages.error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const isSuccess = submitMessage.includes('Thank')

  return (
    <PageLayout title={content.contact.seo.title} description={content.contact.seo.description}>
      {/* Hero Header */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pattern-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />

        <div className="relative content-container text-center">
          <Badge variant="outline" className="mb-6 border-primary/50 bg-primary/10 text-primary px-4 py-1.5">
            <Mail className="mr-2 h-3.5 w-3.5" />
            Get in Touch
          </Badge>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            {content.contact.heading}
          </h1>

          <Separator className="w-16 bg-primary mx-auto mb-6" />

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding pt-8">
        <div className="content-container">
          <div className="grid lg:grid-cols-5 gap-8 max-w-6xl mx-auto">

            {/* Contact Form - Takes 3 columns */}
            <div className="lg:col-span-3">
              <Card className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{content.contact.form.heading}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-foreground">
                          {content.contact.form.fields.name.label}
                        </Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder={content.contact.form.fields.name.placeholder}
                            className="pl-10 bg-secondary/50 border-border focus:border-primary"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-foreground">
                          {content.contact.form.fields.email.label}
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder={content.contact.form.fields.email.placeholder}
                            className="pl-10 bg-secondary/50 border-border focus:border-primary"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-foreground">
                        {content.contact.form.fields.phone.label}
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder={content.contact.form.fields.phone.placeholder}
                          className="pl-10 bg-secondary/50 border-border focus:border-primary"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-foreground">
                        {content.contact.form.fields.message.label}
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder={content.contact.form.fields.message.placeholder}
                        className="bg-secondary/50 border-border focus:border-primary resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-12 text-base cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          {content.contact.form.buttons.submitting}
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          {content.contact.form.buttons.submit}
                        </>
                      )}
                    </Button>

                    {submitMessage && (
                      <Alert
                        variant={isSuccess ? 'default' : 'destructive'}
                        className={isSuccess ? 'border-green-500/50 bg-green-500/10' : ''}
                      >
                        {isSuccess ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        <AlertDescription className={isSuccess ? 'text-green-500' : ''}>
                          {submitMessage}
                        </AlertDescription>
                      </Alert>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info - Takes 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Information Card */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{content.contact.info.heading}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Manager */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{content.contact.info.manager.label}</p>
                      <p className="font-medium text-foreground">{config.managerName}</p>
                    </div>
                  </div>

                  <Separator />

                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{content.contact.info.phone.label}</p>
                      <a
                        href={`tel:${config.phoneNumber}`}
                        className="font-medium text-primary hover:text-primary/80 transition-colors"
                      >
                        {config.phoneNumber}
                      </a>
                    </div>
                  </div>

                  <Separator />

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{content.contact.info.email.label}</p>
                      <a
                        href={`mailto:${config.emailServerToEmail}`}
                        className="font-medium text-primary hover:text-primary/80 transition-colors break-all"
                      >
                        {config.emailServerToEmail}
                      </a>
                    </div>
                  </div>

                  <Separator />

                  {/* Social */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <Facebook className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{content.contact.info.social.label}</p>
                      <a
                        href={config.facebookLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:text-primary/80 transition-colors"
                      >
                        {content.contact.info.social.facebook}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Location Card */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{content.contact.location.heading}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {content.contact.location.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 text-primary" />
                    <span>Bar open daily 4-10pm (closed Mondays)</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
